import { COLLECTION_LAYER } from "@shared/types/collection";
// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
export function toLocalDateKey(iso) {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function formatDateHeader(dateKey) {
    const d = new Date(dateKey + "T00:00:00");
    return { day: DAY_NAMES[d.getDay()], date: `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}` };
}
/** Short label for date badges on individual cards (e.g. "Mar 23"). */
export function formatDateBadge(iso) {
    const d = new Date(iso);
    return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
}
/**
 * Last activity for ordering collections in the feed.
 *
 * For layer-1 Collections this is the raw activity timestamp from
 * the API. For Stacks (layer 2), the `<Stream>` consumer pre-overlays
 * `lastAddedAt` with the max `surfaceAt` across the Stack's loaded
 * members (via `useStackNewestMemberAt`) BEFORE the items reach
 * `buildFeedItems` — so this helper stays a single, layer-agnostic
 * function: it always reads `lastAddedAt`, and whether that came
 * from the API or from the per-stack overlay is the consumer's
 * concern. See `production/web-client/viewer/src/components/stream/use-stack-newest-member-at.ts`
 * for the Stack rule and rationale.
 */
export function collectionActivityTimestamp(collection) {
    return collection.lastAddedAt ?? collection.createdAt;
}
// ---------------------------------------------------------------------------
// Build feed items from blocks + collections
// ---------------------------------------------------------------------------
export function buildFeedItems(envelopes, collections) {
    const items = [];
    for (const envelope of envelopes) {
        // `surfaceAt` is the canonical timeline-sort key. It defaults to
        // `capturedAt` on insert (per `BlockEntity.create`), gets bumped
        // by reminders / manual surface, and is enforced NOT NULL at the
        // DB layer — so a fallback to `capturedAt` would be dead code.
        const sortAt = envelope.block.surfaceAt;
        items.push({ kind: "block", envelope, sortAt });
    }
    if (collections) {
        for (const collection of collections) {
            const sortAt = collectionActivityTimestamp(collection);
            items.push({ kind: "collection", collection, sortAt });
        }
    }
    return items;
}
// ---------------------------------------------------------------------------
// Strategy: group by day
// ---------------------------------------------------------------------------
export const groupByDay = (items) => {
    const groups = new Map();
    for (const item of items) {
        const dateKey = toLocalDateKey(item.sortAt);
        const existing = groups.get(dateKey);
        if (existing)
            existing.push(item);
        else
            groups.set(dateKey, [item]);
    }
    // Sort groups newest-first, items within each group newest-first
    const entries = Array.from(groups.entries()).sort(([a], [b]) => b.localeCompare(a));
    return entries.map(([dateKey, dayItems]) => {
        dayItems.sort((a, b) => b.sortAt.localeCompare(a.sortAt));
        const header = formatDateHeader(dateKey);
        return { key: dateKey, label: `${header.day} ${header.date}`, items: dayItems };
    });
};
// ---------------------------------------------------------------------------
// Strategy: group by week (placeholder)
// ---------------------------------------------------------------------------
export const groupByWeek = (items) => {
    // Placeholder: delegates to day grouping for now
    return groupByDay(items);
};
// ---------------------------------------------------------------------------
// Strategy: group by month (placeholder)
// ---------------------------------------------------------------------------
export const groupByMonth = (items) => {
    // Placeholder: delegates to day grouping for now
    return groupByDay(items);
};
// ---------------------------------------------------------------------------
// Strategy: no grouping (flat list)
// ---------------------------------------------------------------------------
/**
 * Flat layout that preserves the caller's insertion order — no
 * date-based sort. The day / week / month strategies are where you go
 * if you want recency ordering; `"none"` is for surfaces that have
 * their own meaningful order (e.g. the smart-collection tab's rerank
 * order: most-relevant-to-query first, oldest-or-least-relevant last,
 * with "show more" results appended at the bottom). Re-sorting here
 * would clobber that semantics.
 */
export const groupByNone = (items) => {
    return [{ key: "__all__", label: "", items: [...items] }];
};
// ---------------------------------------------------------------------------
// Strategy resolver
// ---------------------------------------------------------------------------
export function resolveGroupStrategy(groupBy) {
    switch (groupBy) {
        case "day": return groupByDay;
        case "week": return groupByWeek;
        case "month": return groupByMonth;
        case "none": return groupByNone;
    }
}
// ---------------------------------------------------------------------------
// Same-collection grouping (synthetic stacks)
// ---------------------------------------------------------------------------
/**
 * Minimum block count required to wrap a same-collection run in a
 * synthetic stack tile. `1` means EVERY block that lives in a
 * groupable (non-primary) Collection gets rendered as a synthetic
 * stack on the homepage, even when it's the only capture from that
 * collection on the day. The trade-off is intentional: a singleton
 * block still belongs to a Collection, and the homepage feed reads
 * more uniformly when those captures show up as the same "branded"
 * card shape (collection palette, title pill, lip avatar) as a
 * multi-block grouping rather than flipping between bare-block cards
 * and stack-shaped cards based on per-day count.
 */
const SYNTHETIC_STACK_MIN_COUNT = 1;
/**
 * Skip grouping for these collection types — they don't carry the
 * "captured into a named collection" semantic the synthetic stack is
 * meant to represent:
 *   - `"primary"` — the user's default bucket; blocks sitting there
 *                   are uncategorized, so collapsing them under a
 *                   "Primary" pill would just hide individual cards
 *                   behind a meaningless label.
 */
function isGroupableCollection(collection) {
    return collection.type !== "primary";
}
/**
 * Resolve a block's *effective* containing collection for grouping
 * purposes — the collection the user thinks of the block as belonging
 * to, which is the same one its lip points at.
 *
 * If the block's direct `collectionId` is a Stack (layer 2), walk up
 * one level to the Stack's parent Collection (layer 1). The Stack
 * itself isn't a meaningful grouping key for the homepage feed: from
 * the user's mental model a block in Stack X inside Collection C is
 * "in C" — and the lip already renders C's title for that reason.
 * Grouping under the Stack would split blocks the user organized
 * together into separate synthetic tiles per Stack, which is the
 * opposite of what the homepage feed is trying to declutter.
 *
 * Returns `undefined` when no groupable parent is reachable (cache
 * miss on either hop, the Stack has no parent, or the resolved
 * collection is primary).
 */
function resolveEffectiveGroupParent(directCollectionId, lookupCollection) {
    const direct = lookupCollection(directCollectionId);
    if (direct === undefined)
        return undefined;
    if (direct.layerIndex === COLLECTION_LAYER.stack) {
        const parentId = direct.parentCollectionId;
        if (typeof parentId !== "string" || parentId.length === 0)
            return undefined;
        const parent = lookupCollection(parentId);
        if (parent === undefined)
            return undefined;
        // Stack parented directly to a Primary Group (relaxed 2026-05-21
        // hierarchy — see `LEGAL_PARENT_CHILD_LAYER_PAIRS`): the Stack
        // itself is the groupable identity. There's no intermediate
        // Collection to walk up to, and Primary is filtered as
        // non-groupable below — without this branch, blocks moved into
        // a Primary-rooted Stack (e.g. the homepage Stream's drag-to-
        // create-stack gesture) would fall through as bare cards rather
        // than rendering as a Stack tile inline.
        if (parent.type === "primary")
            return direct;
        if (isGroupableCollection(parent) === false)
            return undefined;
        return parent;
    }
    if (isGroupableCollection(direct) === false)
        return undefined;
    return direct;
}
/**
 * Walk each day-group's items and wrap every block that lives in a
 * groupable (non-primary) collection into a `synthetic-stack` item —
 * even singletons. Multiple blocks from the same collection on the
 * same day collapse into a single tile.
 *
 * Rules:
 *   - Only same-day. Different days are already visually separated,
 *     and the user wants today's clutter collapsed without hiding
 *     history.
 *   - ≥1 block from the same effective parent collection on the same
 *     day → synthetic stack. (See `SYNTHETIC_STACK_MIN_COUNT` for the
 *     rationale on the singleton case.)
 *   - The synthetic's position in the day is the position of its
 *     newest constituent block (the items remain newest-first sorted).
 *   - `collection`-kind items pass through untouched — Stack tiles
 *     and layer-1 Collection cards already represent their own
 *     grouping.
 *   - Blocks whose collection can't be resolved (cross-layer cache
 *     miss) or whose collection is primary fall through as individual
 *     blocks — the safer, less-surprising failure mode.
 *
 * The `lookupCollection` callback returns the real `CollectionItem`
 * for a given `block.collectionId`. The Stream supplies this by
 * subscribing to `collectionStore.useListAllForSlugLookup()` once and
 * building a Map; callers in tests can pass an inline Map.get.
 */
export function mergeSameCollectionBlocks(groups, lookupCollection) {
    return groups.map((group) => {
        // Pre-resolve each block's *effective* grouping collection — for
        // blocks whose direct `collectionId` points at a Stack, this is
        // the Stack's parent Collection (matches what the block's lip
        // shows). Cached so we don't double-walk in pass 2.
        const effectiveParentByBlockId = new Map();
        // Pass 1 — bucket eligible blocks by their effective parent
        // collection id. Items in `group.items` are already sorted
        // newest-first, so each bucket inherits that ordering naturally.
        const blockBucketsByCollectionId = new Map();
        for (const item of group.items) {
            if (item.kind !== "block")
                continue;
            const directCollectionId = item.envelope.block.collectionId;
            if (typeof directCollectionId !== "string" || directCollectionId.length === 0) {
                continue;
            }
            const effectiveParent = resolveEffectiveGroupParent(directCollectionId, lookupCollection);
            if (effectiveParent === undefined)
                continue;
            const blockId = item.envelope.block.id;
            effectiveParentByBlockId.set(blockId, effectiveParent);
            let bucket = blockBucketsByCollectionId.get(effectiveParent.id);
            if (bucket === undefined) {
                bucket = [];
                blockBucketsByCollectionId.set(effectiveParent.id, bucket);
            }
            bucket.push(item.envelope);
        }
        // Pass 2 — re-walk the original item list and emit:
        //   - non-block items at their original position
        //   - the first eligible block of each bucket whose size meets
        //     `SYNTHETIC_STACK_MIN_COUNT` as a synthetic-stack at that
        //     position (later blocks in the same bucket are skipped —
        //     they're already represented inside the synthetic)
        //   - blocks whose effective parent didn't resolve (or whose
        //     parent collection was primary) pass through as individual
        //     block cards — the fallback for the few cross-layer cache
        //     edge cases.
        // This preserves the newest-first ordering of the input.
        const out = [];
        const emittedCollectionIds = new Set();
        for (const item of group.items) {
            if (item.kind !== "block") {
                out.push(item);
                continue;
            }
            const blockId = item.envelope.block.id;
            const effectiveParent = effectiveParentByBlockId.get(blockId);
            if (effectiveParent === undefined) {
                out.push(item);
                continue;
            }
            const effectiveCollectionId = effectiveParent.id;
            const bucket = blockBucketsByCollectionId.get(effectiveCollectionId);
            if (bucket === undefined || bucket.length < SYNTHETIC_STACK_MIN_COUNT) {
                out.push(item);
                continue;
            }
            if (emittedCollectionIds.has(effectiveCollectionId))
                continue;
            emittedCollectionIds.add(effectiveCollectionId);
            const newest = bucket[0].block.surfaceAt;
            const dateKey = toLocalDateKey(newest);
            out.push({
                kind: "synthetic-stack",
                id: `synthetic-stack:${effectiveCollectionId}:${dateKey}`,
                parent: effectiveParent,
                blocks: bucket,
                sortAt: newest,
            });
        }
        return { key: group.key, label: group.label, items: out };
    });
}
